import React from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import NotificationsOffRoundedIcon from "@mui/icons-material/NotificationsOffRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  ColorSetting,
  EditorGroup,
  PageEditorShell,
  SelectSetting,
  TextSetting,
  ToggleSetting,
} from "./PageEditorControls";

const DEFAULT_PROMPT = Object.freeze({
  enabled: true,
  title: "Recibí novedades",
  subscribedTitle: "Notificaciones activadas",
  description:
    "Activá los avisos de {businessName} para enterarte de novedades y promociones.",
  subscribedDescription: "Este navegador recibe avisos de {businessName}.",
  enableLabel: "Activar notificaciones",
  disableLabel: "Desactivar",
  dismissLabel: "Ahora no",
  processingLabel: "Procesando…",
  successMessage: "Notificaciones activadas correctamente.",
  disableSuccessMessage: "Notificaciones desactivadas.",
  errorMessage: "No se pudieron activar las notificaciones.",
  disableErrorMessage: "No se pudieron desactivar las notificaciones.",
  position: "bottom-right",
  backgroundColor: "",
  textColor: "",
  mutedTextColor: "",
});

function promptValue(form) {
  return form?.notifications?.webPushPrompt || {};
}

function value(prompt, key) {
  return prompt?.[key] ?? DEFAULT_PROMPT[key];
}

function previewText(raw, businessName) {
  return String(raw ?? "").replace(/\{businessName\}/g, businessName);
}

function PromptCard({ prompt, theme, businessName, subscribed = false }) {
  const backgroundColor = value(prompt, "backgroundColor") || theme.surfaceColor || "#FFFFFF";
  const textColor = value(prompt, "textColor") || theme.textColor || "#1A1A2E";
  const mutedTextColor = value(prompt, "mutedTextColor") || theme.mutedTextColor || "#5B5D73";
  const title = subscribed ? value(prompt, "subscribedTitle") : value(prompt, "title");
  const description = subscribed
    ? value(prompt, "subscribedDescription")
    : value(prompt, "description");

  return (
    <Paper
      elevation={10}
      sx={{
        width: 390,
        maxWidth: "calc(100% - 24px)",
        borderRadius: 3,
        p: 2,
        border: "1px solid rgba(25,36,47,.12)",
        bgcolor: backgroundColor,
        color: textColor,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(25,36,47,.07)",
            flexShrink: 0,
          }}
        >
          <NotificationsActiveRoundedIcon color="primary" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, color: "inherit" }}>
            {previewText(title, businessName)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.35, color: mutedTextColor }}>
            {previewText(description, businessName)}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant={subscribed ? "outlined" : "contained"}
              startIcon={subscribed ? <NotificationsOffRoundedIcon /> : <NotificationsActiveRoundedIcon />}
            >
              {subscribed ? value(prompt, "disableLabel") : value(prompt, "enableLabel")}
            </Button>
            <Button size="small" color="inherit" startIcon={<CloseRoundedIcon />}>
              {value(prompt, "dismissLabel")}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function WebPushPromptPreview({ form }) {
  const prompt = promptValue(form);
  const theme = form?.theme || {};
  const businessName = form?.business?.name || "Mi negocio";
  const alignItems = value(prompt, "position") === "bottom-left" ? "flex-start" : "flex-end";

  return (
    <Box
      sx={{
        minHeight: 700,
        bgcolor: theme.backgroundColor || "#EEF0F5",
        p: { xs: 1.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems,
        gap: 2,
      }}
    >
      {prompt.enabled === false ? (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, maxWidth: 460 }}>
          <Typography sx={{ fontWeight: 900 }}>Prompt oculto</Typography>
          <Typography variant="body2" sx={{ mt: 0.6, color: "text.secondary" }}>
            Web Push puede seguir activo técnicamente; este ajuste sólo evita mostrar la invitación pública.
          </Typography>
        </Paper>
      ) : (
        <>
          <Box sx={{ width: "100%", display: "flex", justifyContent: alignItems === "flex-start" ? "flex-start" : "flex-end" }}>
            <Stack spacing={0.7} alignItems={alignItems}>
              <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(25,36,47,.58)" }}>
                Estado suscripto
              </Typography>
              <PromptCard prompt={prompt} theme={theme} businessName={businessName} subscribed />
            </Stack>
          </Box>
          <Box sx={{ width: "100%", display: "flex", justifyContent: alignItems === "flex-start" ? "flex-start" : "flex-end" }}>
            <Stack spacing={0.7} alignItems={alignItems}>
              <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(25,36,47,.58)" }}>
                Estado inicial
              </Typography>
              <PromptCard prompt={prompt} theme={theme} businessName={businessName} />
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
}

export default function WebPushPromptEditor({ form, device, saving = false, onCommit }) {
  const prompt = promptValue(form);
  const set = React.useCallback(
    (key, nextValue) => onCommit?.(`notifications.webPushPrompt.${key}`, nextValue),
    [onCommit],
  );

  return (
    <PageEditorShell
      title="Notificaciones"
      description="Configurá sólo la presentación del prompt Web Push. La suscripción técnica, permisos y endpoints no se modifican desde acá."
      device={device}
      saving={saving}
      preview={<WebPushPromptPreview form={form} />}
    >
      <EditorGroup
        title="Visibilidad"
        description="Ocultar este prompt no deshabilita Web Push ni elimina suscripciones existentes."
      >
        <ToggleSetting
          label="Mostrar invitación Web Push"
          checked={prompt.enabled}
          onChange={(nextValue) => set("enabled", nextValue)}
        />
        <SelectSetting
          label="Posición"
          value={value(prompt, "position")}
          onChange={(nextValue) => set("position", nextValue)}
          options={[
            { value: "bottom-right", label: "Abajo a la derecha" },
            { value: "bottom-left", label: "Abajo a la izquierda" },
          ]}
        />
      </EditorGroup>

      <EditorGroup title="Estado inicial">
        <TextSetting label="Título" value={value(prompt, "title")} onChange={(nextValue) => set("title", nextValue)} />
        <TextSetting
          label="Descripción"
          value={value(prompt, "description")}
          onChange={(nextValue) => set("description", nextValue)}
          multiline
          helperText="Podés usar {businessName} para insertar el nombre del negocio."
        />
        <TextSetting label="Botón activar" value={value(prompt, "enableLabel")} onChange={(nextValue) => set("enableLabel", nextValue)} />
      </EditorGroup>

      <EditorGroup title="Estado suscripto">
        <TextSetting label="Título suscripto" value={value(prompt, "subscribedTitle")} onChange={(nextValue) => set("subscribedTitle", nextValue)} />
        <TextSetting
          label="Descripción suscripto"
          value={value(prompt, "subscribedDescription")}
          onChange={(nextValue) => set("subscribedDescription", nextValue)}
          multiline
          helperText="Podés usar {businessName}."
        />
        <TextSetting label="Botón desactivar" value={value(prompt, "disableLabel")} onChange={(nextValue) => set("disableLabel", nextValue)} />
      </EditorGroup>

      <EditorGroup title="Acciones y mensajes">
        <TextSetting label="Descartar / ahora no" value={value(prompt, "dismissLabel")} onChange={(nextValue) => set("dismissLabel", nextValue)} />
        <TextSetting label="Procesando" value={value(prompt, "processingLabel")} onChange={(nextValue) => set("processingLabel", nextValue)} />
        <TextSetting label="Éxito al activar" value={value(prompt, "successMessage")} onChange={(nextValue) => set("successMessage", nextValue)} />
        <TextSetting label="Éxito al desactivar" value={value(prompt, "disableSuccessMessage")} onChange={(nextValue) => set("disableSuccessMessage", nextValue)} />
        <TextSetting label="Error al activar" value={value(prompt, "errorMessage")} onChange={(nextValue) => set("errorMessage", nextValue)} />
        <TextSetting label="Error al desactivar" value={value(prompt, "disableErrorMessage")} onChange={(nextValue) => set("disableErrorMessage", nextValue)} />
      </EditorGroup>

      <EditorGroup
        title="Presentación"
        description="Vacío mantiene los colores del tema publicado."
      >
        <ColorSetting label="Fondo" value={prompt.backgroundColor} onChange={(nextValue) => set("backgroundColor", nextValue)} />
        <ColorSetting label="Texto principal" value={prompt.textColor} onChange={(nextValue) => set("textColor", nextValue)} />
        <ColorSetting label="Texto secundario" value={prompt.mutedTextColor} onChange={(nextValue) => set("mutedTextColor", nextValue)} />
      </EditorGroup>
    </PageEditorShell>
  );
}
